import React from "react";
import Modal from "react-modal";
import Button, { ButtonThemes } from "ui-components/button/button";
import XGSIcon from "ui-components/icon/xgsIcon";
import XGSIcons from "ui-components/icon/xgsIcons";
import "../../../sass/modal.scss";
import "./closeDoorModal.scss";

Modal.setAppElement("#root");

// Custom centered modal style
const centeredModalStyle = {
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    zIndex: 1001,
    position: "fixed" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    position: "relative" as const,
    top: "auto",
    left: "auto",
    right: "auto",
    bottom: "auto",
    margin: "auto",
    transform: "none",
    padding: "24px",
    maxWidth: "520px",
    maxHeight: "90vh",
    width: "100%",
    border: "1px solid #E0E0E0",
    borderRadius: "4px",
    background: "#fff",
    overflow: "auto",
  }
};

interface CloseDoorModalProps {
  isOpen: boolean;
  onClose: () => void;
  manifestNumber?: string;
  doorNumber?: string;
  destinationTerminal?: string;
  pieces?: number;
  piecesUnloaded?: number;
  weight?: number;
  isSubmitting?: boolean;
  onConfirm: () => void;
}

const formatDoorLabel = (doorNumber?: string) => {
  if (!doorNumber) return "-";
  const normalized = doorNumber.toString().toUpperCase();
  return normalized.startsWith("D-") ? normalized : `D-${normalized}`;
};

const CloseDoorModal: React.FC<CloseDoorModalProps> = ({
  isOpen,
  onClose,
  manifestNumber,
  doorNumber,
  destinationTerminal,
  pieces,
  piecesUnloaded,
  weight,
  isSubmitting,
  onConfirm,
}) => {
  const piecesCount = pieces != null && pieces !== "" ? Number(pieces) : 0;
  const unloadingPercent =
    piecesCount > 0 && typeof piecesUnloaded === "number"
      ? Math.min(100, Math.round((piecesUnloaded / piecesCount) * 100))
      : 100;
  const remainingItems = piecesCount > 0 && typeof piecesUnloaded === "number"
    ? Math.max(0, piecesCount - piecesUnloaded)
    : 0;
  const showWarning = unloadingPercent < 100 && remainingItems > 0;

  return (
    <Modal
      isOpen={isOpen}
      style={centeredModalStyle}
      onRequestClose={onClose}
      className="close-door-modal"
      overlayClassName="close-door-modal-overlay"
      ariaHideApp={false}
    >
      <div className="xgs-modal__header">
        <div className="close-door-modal__title">Close Door?</div>
        <button
          className="xgs-modal__close"
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
            fontSize: "20px",
            color: "#666",
          }}
        >
          ×
        </button>
      </div>
      <div className="xgs-modal__content">
        <div className="close-door-modal__message">
          Once the door is closed, unloading will be marked as complete. You won't be able to edit door, trailer, or freight details.
        </div>

        {showWarning && (
          <div className="close-door-modal__warning">
            <div className="close-door-modal__warning-header">
              <XGSIcon
                icon={XGSIcons.faExclamationTriangle}
                className="close-door-modal__warning-icon"
              />
              <div className="close-door-modal__warning-title">Freight still remains on the trailer</div>
            </div>
            <div className="close-door-modal__warning-content">
              <p className="close-door-modal__warning-text">
                Unloading is currently <strong>{unloadingPercent}%</strong> complete. There are still{" "}
                <strong>{remainingItems} items</strong> remaining on the trailer.
              </p>
              <p className="close-door-modal__warning-consequence">
                If you close the door now, the remaining freight will not be scannable at this terminal and must be handled through the exception process.
              </p>
            </div>
          </div>
        )}

        <div className="close-door-modal__summary">
          <div className="close-door-modal__summary-title">Load Summary</div>
          <div className="close-door-modal__summary-card">
            <div className="close-door-modal__summary-content">
              <div className="close-door-modal__summary-row">
                <span className="label">Manifest:</span>
                <span className="value">{manifestNumber || "-"}</span>
              </div>

              <div className="close-door-modal__summary-row">
                <span className="label">Door:</span>
                <span className="value">{formatDoorLabel(doorNumber)}</span>
              </div>

              <div className="close-door-modal__summary-row">
                <span className="label">Destination:</span>
                <span className="value">{destinationTerminal || "-"}</span>
              </div>

              <div className="close-door-modal__summary-row">
                <span className="label">Unload %:</span>
                <span className="value">
                  {piecesCount > 0 && typeof piecesUnloaded === "number"
                    ? `${unloadingPercent}%`
                    : "-"}
                </span>
              </div>

              <div className="close-door-modal__summary-row">
                <span className="label">Pieces:</span>
                <span className="value">{pieces ?? "-"}</span>
              </div>

              <div className="close-door-modal__summary-row">
                <span className="label">Weight:</span>
                <span className="value">
                  {weight != null ? `${weight.toLocaleString("en-US")} lbs` : "-"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="xgs-modal__buttons" style={{ textAlign: "right", marginTop: "24px" }}>
          <Button
            type="button"
            theme={ButtonThemes.gray}
            onClick={onClose}
            className="xgs-modal__button"
          >
            Cancel
          </Button>
          <Button
            type="button"
            theme={showWarning ? ButtonThemes.red : ButtonThemes.blue}
            onClick={onConfirm}
            className="xgs-modal__button"
            spinner={isSubmitting}
            disabled={isSubmitting}
          >
            {showWarning ? "Close Door Anyway" : "Close Door"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CloseDoorModal;

